<script lang="ts" setup>
  import { useHttpClient, type FormContextInterface, setModelWithContext, useRoute } from '@othercommerce/foundation';
  import type { Complaint } from '@storefront/resources';

  interface Props {
    complaint: Complaint;
  }

  const props = defineProps<Props>();

  const route = useRoute();
  const http = useHttpClient();

  function actionClear(ctx: FormContextInterface) {
    setModelWithContext('message', ctx, null);
    setModelWithContext('files', ctx, []);
  }

  function handleSubmit(data: any, ctx: FormContextInterface) {
    const formData = new FormData();

    formData.append('message', data.message ?? '');

    const files = data.files instanceof FileList
      ? Array.from(data.files)
      : Array.isArray(data.files)
        ? data.files
        : [];

    for (const file of files) {
      formData.append('files[]', file);
    }

    const request = http.post(
      route('web.complaint.message.store', { complaint: props.complaint }),
      formData,
    );

    return request.then(() => { actionClear(ctx);});
  }
</script>
<template>
  <section class="chat">
    <SfTile>
      <h2 class="chat__heading">{{ $t('SHOP.COMMON.HEADER.CHAT') }}</h2>
      <FormController v-slot="{ processing }" @submit="handleSubmit">
        <div class="chat__form">
          <div class="chat__control">
            <div class="chat__attachment">
              <SfAttachmentInput multiple name="files" />
            </div>
            <SfFormTextarea :label="$t('SHOP.COMMON.ACTION.SEND_MESSAGE')" name="message" rows="1" />
          </div>
          <div class="chat__submit">
            <SfButton :loading="processing" primary type="submit">
              <template #default>
                {{ $t('SHOP.COMMON.ACTION.SEND') }}
              </template>
              <template #after>
                <SfIconSend aria-hidden="true" />
              </template>
            </SfButton>
          </div>
        </div>
      </FormController>
      <div v-if="complaint.messages && complaint.messages.length > 0" class="chat__messages">
        <ComplaintChatMessage v-for="message in complaint.messages" :key="message.id" :complaint :message />
      </div>
    </SfTile>
  </section>
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .chat {
    flex-grow: 0;
    width: 100%;

    :deep(textarea) {
      &.control {
        min-height: 3rem;
      }
    }

    &__heading {
      font-size: 1.25rem;
    }

    &__form {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      width: 100%;

      @include media.query(sm) {
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.5rem;
      }
    }

    &__control {
      display: flex;
      width: 100%;
      align-items: center;
      gap: 1rem;

      @include media.query(sm) {
        gap: 0.5rem;
      }
    }

    &__messages {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      @include media.query(md) {
        gap: 0.5rem;
      }
    }
  }
</style>
